/**
 * Feedback List Component
 * Shows feedback messages for sellers/landlords in their portal
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  MessageSquare, Mail, MailOpen, Clock, User, ChevronRight, 
  Loader2, Inbox 
} from "lucide-react";
import { API } from "@/App";

const FeedbackList = ({ siMode = false }) => {
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const token = localStorage.getItem("token") || localStorage.getItem("portal_token");

  useEffect(() => {
    fetchFeedback();
  }, [siMode]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      // Use SI endpoint if in SI mode
      const endpoint = siMode 
        ? `${API}/sales-instructions/seller/my-feedback`
        : `${API}/feedback/my`;
      
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback || []);
      setUnreadCount(res.data.unread || 0);
    } catch (error) {
      console.error("Error fetching feedback:", error);
      if (error.response?.status !== 403) {
        // Don't show error in SI mode if fallback
        if (!siMode) {
          toast.error("Failed to load feedback");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewFeedback = async (item) => {
    setSelectedFeedback(item);
    setViewModalOpen(true);
    
    // Mark as read if unread
    if (!item.read) {
      try {
        await axios.patch(
          `${API}/feedback/${item.feedback_id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state
        setFeedback(prev => prev.map(f => 
          f.feedback_id === item.feedback_id 
            ? { ...f, read: true, read_at: new Date().toISOString() }
            : f
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error("Error marking feedback as read:", error);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getSubjectColor = (subject) => {
    switch (subject) {
      case "Viewing Feedback":
        return "bg-blue-100 text-blue-700";
      case "Weekly Update":
        return "bg-emerald-100 text-emerald-700";
      case "General Update":
        return "bg-slate-100 text-slate-700";
      default:
        return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold">Feedback from Your Agent</h2>
            <p className="text-sm text-muted-foreground">
              Updates and feedback about your property
            </p>
          </div>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-red-500 text-white">
            {unreadCount} unread
          </Badge>
        )}
      </div>

      {/* Feedback List */}
      {feedback.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Inbox className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No Feedback Yet</h3>
            <p className="text-muted-foreground mt-1">
              Your agent will send you updates here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {feedback.map((item) => (
            <Card 
              key={item.feedback_id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${!item.read ? "border-l-4 border-l-blue-500" : ""}`}
              onClick={() => handleViewFeedback(item)}
              data-testid={`feedback-item-${item.feedback_id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-full ${item.read ? "bg-slate-100" : "bg-blue-100"}`}>
                      {item.read ? (
                        <MailOpen className="w-5 h-5 text-slate-500" />
                      ) : (
                        <Mail className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getSubjectColor(item.subject)}>
                          {item.subject}
                        </Badge>
                        {!item.read && (
                          <Badge variant="outline" className="text-xs">New</Badge>
                        )}
                      </div>
                      <p className={`text-sm ${item.read ? "text-slate-600" : "text-slate-900 font-medium"}`}>
                        {item.message.length > 100 
                          ? item.message.substring(0, 100) + "..." 
                          : item.message}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {item.sender_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* View Feedback Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              {selectedFeedback?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground border-b pb-4">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  From: {selectedFeedback.sender_name}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatDate(selectedFeedback.created_at)}
                </span>
              </div>
              
              <div className="whitespace-pre-wrap text-slate-700">
                {selectedFeedback.message}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackList;
